import { type AccessDeclaration, isRecognisedDeclaration } from './access';
import { paths } from './paths';

/**
 * The start-up audit: a route that has not said who may see it stops the app in
 * development, exactly as an undeclared route stops the API from booting.
 *
 * The API's rule (R-108) is in two halves and neither is redundant. A guard
 * refuses the undeclared route at request time; a boot audit refuses to start an
 * application containing one at all. Without the second, a forgotten declaration
 * ships and is discovered by somebody being refused something they should have
 * had — or is never discovered, because nobody tried.
 *
 * The browser has three halves, because TypeScript can do something Nest's
 * decorators cannot:
 *
 * 1. **Compile time.** `access` is a required field of `RouteDefinition`, so a
 *    route table with an undeclared route does not typecheck. This is the
 *    strongest of the three and it is the one that will actually fire.
 * 2. **Start-up.** This file, run when the route table module is first imported
 *    — which is before anything can render, and which `pnpm test` reaches too,
 *    because the existing suite imports the app. It is the backstop for what a
 *    type cannot see: a cast, a route table built at runtime, two packages built
 *    against different versions of this one.
 * 3. **Render time.** The guard refuses an undeclared route and says so on
 *    screen. See `RouteGuard.tsx`.
 *
 * **Why development throws and production does not.** The API's audit runs at
 * deploy time on a server: a failed boot is a rollback and nobody at the park
 * notices. A browser app "boots" on the user's machine at the moment they need
 * it, so throwing there is not a rollback, it is the whole back-office dark —
 * sign-in included — because of a mistake on one route. The honest mirror of
 * "refuses to boot" for a client is "refuses to ship", and that is development
 * and the test run. In production the route is still refused, still named in the
 * console, and still says on screen what is wrong with it. What it must never be
 * is quiet: a blank screen reads as a bug in the screen, gets "fixed" by
 * loosening the guard, and nobody learns a declaration was missing.
 */

/** A route as the audit needs to see it — deliberately weaker than the real type. */
export interface AuditableRoute {
  readonly path: string;

  /**
   * Optional and nullable *here* and required on `RouteDefinition`. The audit
   * exists for the values the type system did not see, so its own input must be
   * able to express them.
   */
  readonly access?: AccessDeclaration | null;
}

/** Something wrong with a route's declaration, and what to do about it. */
export interface RouteDefect {
  readonly path: string;
  readonly reason: string;
  readonly remedy: string;
}

/**
 * Every defect in `routes`, in table order.
 *
 * Every offender is reported rather than the first, because stopping early turns
 * one start-up into a queue of them, each revealing the next mistake.
 */
export function findRouteDefects(routes: readonly AuditableRoute[]): RouteDefect[] {
  const defects: RouteDefect[] = [];
  const seen = new Set<string>();

  for (const route of routes) {
    if (seen.has(route.path)) {
      // Two entries for one path is a live way to bypass the guard: the router
      // matches one of them, and which one is a detail of table order that
      // nobody reading the second entry can see. A route added twice — once
      // public during development and once guarded — is refused rather than
      // resolved in whichever direction the router happens to prefer.
      defects.push({
        path: route.path,
        reason: 'is declared more than once, so which declaration applies is not readable',
        remedy: 'Remove the duplicate. One path, one entry, one declaration.',
      });
    }

    seen.add(route.path);

    if (route.access === undefined || route.access === null) {
      defects.push({
        path: route.path,
        // Deliberately about the route rather than the viewer, because the
        // person who needs to read this is the developer who left it off.
        reason: 'declares no access requirement',
        remedy:
          'Add publicRoute(), signedOutOnly(), requireSignedIn() or requireRole(...) to its entry.',
      });

      continue;
    }

    if (!isRecognisedDeclaration(route.access)) {
      defects.push({
        path: route.path,
        reason: 'declares an access requirement this app does not recognise',
        remedy: 'Use one of the builders in access.ts rather than an object literal or a cast.',
      });

      continue;
    }

    if (route.access.kind === 'role' && route.access.roles.length === 0) {
      // Not a stricter route — an unopenable one. It refuses every caller,
      // renders as a screen nobody can get into, and reads as a broken screen
      // rather than as a declaration that names nobody.
      defects.push({
        path: route.path,
        reason: 'requires a role and names none, so it refuses everybody',
        remedy: 'Name the roles that may reach it, or use requireSignedIn() if any role may.',
      });

      continue;
    }

    if (route.path === paths.changeCredential && !allowsPendingCredential(route.access)) {
      // The credential gate is the one route a user with `mustChangeCredential`
      // is sent to, so it is the one route that must admit them. Declared any
      // other way it redirects to itself: the guard sends them to the gate, the
      // gate's own declaration refuses them, and the browser hangs in a loop
      // that looks like the app freezing rather than like a misdeclared route.
      defects.push({
        path: route.path,
        reason:
          'is the must-change-credential gate but does not admit a pending credential, so it would redirect to itself',
        remedy: 'Declare it requireSignedIn({ allowsPendingCredential: true }).',
      });
    }
  }

  return defects;
}

const allowsPendingCredential = (access: AccessDeclaration): boolean =>
  access.kind === 'signedIn' && access.allowsPendingCredential;

/**
 * The message the app dies with — or, in production, the one it logs.
 *
 * It names every offender and says what to add, because the person reading it is
 * being refused a working app by a rule they have probably just met.
 */
export function describeRouteDefects(defects: readonly RouteDefect[]): string {
  return [
    `${String(defects.length)} route(s) in this app are not safely declared, so it will not start:`,
    ...defects.flatMap((defect) => [
      `  ${defect.path}  ${defect.reason}`,
      `      ${defect.remedy}`,
    ]),
    '',
    'A route that has not stated who may see it is forbidden, not public.',
    'This mirrors the API, which refuses to boot with an undeclared route in it',
    'and refuses the request if one somehow reaches it.',
  ].join('\n');
}

/**
 * Run the audit. Throws in development and in test; logs and carries on in
 * production, where the guard refuses each offending route on screen.
 *
 * `isDevelopment` is a parameter rather than a read of `import.meta.env`, for
 * the same reason the clock is: a rule that consults an ambient global is a rule
 * whose other branch is never exercised.
 */
export function auditRouteTable(routes: readonly AuditableRoute[], isDevelopment: boolean): void {
  const defects = findRouteDefects(routes);

  if (defects.length === 0) return;

  const message = describeRouteDefects(defects);

  if (isDevelopment) throw new Error(message);

  // Not swallowed and not a warning. This is the only signal in production that
  // a route is refusing everybody by omission rather than by decision.
  console.error(message);
}
